import dayjs, { Dayjs } from "dayjs";
import {
  IPayPeriodTemplate,
  PayPeriodTemplateStartDayOfWeek,
} from "@/business/business";
import {
  PAY_PERIOD_TEMPLATE_CADENCE,
  PAY_PERIOD_TEMPLATE_START_DAY_OF_WEEK,
} from "@/business/constants";
import { FORMAT_SIMPLE_MONTH_DAY } from "@/core/utils/date";
import { PayrollPeriod } from "@/spiffs/spiffs.d";

const DAY_OF_WEEK_MAP: Record<PayPeriodTemplateStartDayOfWeek, number> = {
  [PAY_PERIOD_TEMPLATE_START_DAY_OF_WEEK.sunday]: 0,
  [PAY_PERIOD_TEMPLATE_START_DAY_OF_WEEK.monday]: 1,
  [PAY_PERIOD_TEMPLATE_START_DAY_OF_WEEK.tuesday]: 2,
  [PAY_PERIOD_TEMPLATE_START_DAY_OF_WEEK.wednesday]: 3,
  [PAY_PERIOD_TEMPLATE_START_DAY_OF_WEEK.thursday]: 4,
  [PAY_PERIOD_TEMPLATE_START_DAY_OF_WEEK.friday]: 5,
  [PAY_PERIOD_TEMPLATE_START_DAY_OF_WEEK.saturday]: 6,
};

const calculateWeeklyPeriod = (
  date: Dayjs,
  startDayOfWeek: PayPeriodTemplateStartDayOfWeek,
): { start: Dayjs; end: Dayjs } => {
  const targetDay = DAY_OF_WEEK_MAP[startDayOfWeek];
  const currentDay = date.day();

  let daysToSubtract = currentDay - targetDay;
  if (daysToSubtract < 0) {
    daysToSubtract += 7;
  }

  const start = date.subtract(daysToSubtract, "day").startOf("day");
  const end = start.add(6, "day").endOf("day");

  return { start, end };
};

const calculateFirstAndFifteenthPeriod = (
  date: Dayjs,
): { start: Dayjs; end: Dayjs } => {
  if (date.date() <= 15) {
    const start = date.startOf("month");
    const end = date.date(15).endOf("day");
    return { start, end };
  } else {
    const start = date.date(16).startOf("day");
    const end = date.endOf("month");
    return { start, end };
  }
};

const calculateMonthlyPeriod = (date: Dayjs): { start: Dayjs; end: Dayjs } => ({
  start: date.startOf("month"),
  end: date.endOf("month"),
});

const calculateEveryOtherWeekPeriod = (
  date: Dayjs,
  referenceDate: string,
): { start: Dayjs; end: Dayjs } => {
  const reference = dayjs(referenceDate).startOf("day");

  const daysSinceReference = date.diff(reference, "day");

  const cycleNumber = Math.floor(daysSinceReference / 14);

  const start = reference.add(cycleNumber * 14, "day");

  const end = start.add(13, "day").endOf("day");

  return { start, end };
};

export const calculatePeriodBoundaries = (
  date: Dayjs,
  template: IPayPeriodTemplate,
): { start: Dayjs; end: Dayjs } => {
  if (template.cadence === PAY_PERIOD_TEMPLATE_CADENCE.monthly) {
    return calculateMonthlyPeriod(date);
  }

  if (template.cadence === PAY_PERIOD_TEMPLATE_CADENCE.first_and_fifteenth) {
    return calculateFirstAndFifteenthPeriod(date);
  }

  if (template.cadence === PAY_PERIOD_TEMPLATE_CADENCE.every_other_week) {
    return calculateEveryOtherWeekPeriod(
      date,
      template.period_start_reference_date,
    );
  }

  return calculateWeeklyPeriod(date, template.start_day_of_week);
};

export const getSpiffPeriod = (
  spiffEventDate: string,
  template: IPayPeriodTemplate,
): PayrollPeriod => {
  const eventDate = dayjs(spiffEventDate);
  const { start, end } = calculatePeriodBoundaries(eventDate, template);

  return createPayrollPeriod(start, end);
};

const createPayrollPeriod = (start: Dayjs, end: Dayjs): PayrollPeriod => {
  const id = `${start.format("YYYY-MM-DD")}_${end.format("YYYY-MM-DD")}`;
  const displayRange = `${start.format(FORMAT_SIMPLE_MONTH_DAY)} - ${end.format(FORMAT_SIMPLE_MONTH_DAY)}`;
  const status = getStatus(start, end);

  return {
    id,
    start,
    end,
    displayRange,
    status,
    isLocked: false, // Will be set by grouping logic
  };
};

const getStatus = (start: Dayjs, end: Dayjs): "past" | "current" | "future" => {
  const now = dayjs();

  if (now.isAfter(end, "day")) {
    return "past";
  } else if (now.isBefore(start, "day")) {
    return "future";
  } else {
    return "current";
  }
};

export const isPeriodCurrent = (period: PayrollPeriod): boolean =>
  period.status === "current";

export const isPeriodPast = (period: PayrollPeriod): boolean =>
  period.status === "past";

const getNextPeriodStart = (
  currentStart: Dayjs,
  template: IPayPeriodTemplate,
): Dayjs => {
  if (template.cadence === PAY_PERIOD_TEMPLATE_CADENCE.monthly) {
    return currentStart.add(1, "month").startOf("month");
  }

  if (template.cadence === PAY_PERIOD_TEMPLATE_CADENCE.weekly) {
    return currentStart.add(7, "day");
  }

  if (template.cadence === PAY_PERIOD_TEMPLATE_CADENCE.first_and_fifteenth) {
    if (currentStart.date() === 1) {
      return currentStart.date(16);
    }
    return currentStart.add(1, "month").date(1).startOf("day");
  }

  return currentStart.add(14, "day");
};

const getPreviousPeriodStart = (
  currentStart: Dayjs,
  template: IPayPeriodTemplate,
): Dayjs => {
  if (template.cadence === PAY_PERIOD_TEMPLATE_CADENCE.monthly) {
    return currentStart.subtract(1, "month").startOf("month");
  }

  if (template.cadence === PAY_PERIOD_TEMPLATE_CADENCE.weekly) {
    return currentStart.subtract(7, "day");
  }

  if (template.cadence === PAY_PERIOD_TEMPLATE_CADENCE.first_and_fifteenth) {
    if (currentStart.date() === 1) {
      return currentStart.subtract(1, "month").date(16).startOf("day");
    }
    return currentStart.date(1).startOf("day");
  }

  return currentStart.subtract(14, "day");
};

export const generatePayrollPeriods = (
  template: IPayPeriodTemplate,
  pastPeriods: number = 12,
  futurePeriods: number = 4,
): PayrollPeriod[] => {
  const periods: PayrollPeriod[] = [];
  const now = dayjs();

  const { start: currentStart } = calculatePeriodBoundaries(now, template);

  let periodStart = currentStart;
  for (let i = 0; i < pastPeriods; i++) {
    periodStart = getPreviousPeriodStart(periodStart, template);
    const { start, end } = calculatePeriodBoundaries(periodStart, template);
    periods.unshift(createPayrollPeriod(start, end));
  }

  const { start, end } = calculatePeriodBoundaries(now, template);
  periods.push(createPayrollPeriod(start, end));

  periodStart = currentStart;
  for (let i = 0; i < futurePeriods; i++) {
    periodStart = getNextPeriodStart(periodStart, template);
    const { start, end } = calculatePeriodBoundaries(periodStart, template);
    periods.push(createPayrollPeriod(start, end));
  }

  return periods;
};
